import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Bar, Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js"
import API from "../../common/API"
import AdminActivityLogUserDetail from "./AdminActivityLogUserDetail"
import AdminActivityLogOrgDetail from "./AdminActivityLogOrgDetail"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface ModuleRank {
  module: string
  totalActions: number
  uniqueUsers: number
  uniqueOrgs: number
}

interface ActionBreakdown {
  module: string
  action: string
  count: number
}

interface DailyTrend {
  date: string
  count: number
  uniqueUsers: number
}

interface TopUser {
  userId: string
  userName: string
  totalActions: number
  modules: number
}

interface TopOrg {
  organizationId: string
  organizationName: string
  totalActions: number
  uniqueUsers: number
}

interface HourlyActivity {
  hour: number
  count: number
}

interface WeekdayActivity {
  dayOfWeek: number
  dayName: string
  count: number
}

interface AnalyticsData {
  period: { days: number; since: string; until: string }
  totalActions: number
  totalUniqueUsers: number
  totalUniqueOrgs: number
  moduleRanking: ModuleRank[]
  actionBreakdown: ActionBreakdown[]
  dailyTrend: DailyTrend[]
  topUsers: TopUser[]
  topOrganizations: TopOrg[]
  hourlyActivity: HourlyActivity[]
  weekdayActivity: WeekdayActivity[]
}

const moduleColors = [
  "#3b82f6",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
  "#f97316",
  "#ec4899",
  "#22c55e",
  "#eab308",
  "#6366f1",
  "#06b6d4",
]

type DetailView =
  | { type: "user"; userId: string; userName: string }
  | { type: "org"; organizationId: string; organizationName: string }
  | null

const AdminUsageStatisticsComponent = () => {
  const { t } = useTranslation("global")
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [days, setDays] = useState(30)
  const [expandedModule, setExpandedModule] = useState<string | null>(null)
  const [detailView, setDetailView] = useState<DetailView>(null)

  const bindData = useCallback(async () => {
    try {
      setIsLoading(true)
      const res = await API.getAction(
        "dashboard/UsageStatistics",
        `days=${days}`
      )
      if (res.data.status === "ok") {
        setData(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }, [days])

  useEffect(() => {
    bindData()
  }, [bindData])

  if (detailView?.type === "user") {
    return (
      <AdminActivityLogUserDetail
        userId={detailView.userId}
        userName={detailView.userName}
        onBack={() => setDetailView(null)}
      />
    )
  }

  if (detailView?.type === "org") {
    return (
      <AdminActivityLogOrgDetail
        organizationId={detailView.organizationId}
        organizationName={detailView.organizationName}
        onBack={() => setDetailView(null)}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  if (!data) return null

  const maxModuleActions = data.moduleRanking[0]?.totalActions || 1

  // Daily trend chart
  const dailyChartData = {
    labels: data.dailyTrend.map(x => x.date.slice(5)),
    datasets: [
      {
        label: t("Acciones"),
        data: data.dailyTrend.map(x => x.count),
        borderColor: "#3b82f6",
        backgroundColor: "#3b82f620",
        fill: true,
        tension: 0.4,
        pointRadius: 2,
      },
      {
        label: t("Usuarios Únicos"),
        data: data.dailyTrend.map(x => x.uniqueUsers),
        borderColor: "#8b5cf6",
        backgroundColor: "#8b5cf620",
        fill: true,
        tension: 0.4,
        pointRadius: 2,
      },
    ],
  }

  // Hourly bar chart
  const hourlyLabels = Array.from({ length: 24 }, (_, i) => `${i}h`)
  const hourlyData = Array.from({ length: 24 }, (_, h) => {
    const entry = data.hourlyActivity.find(x => x.hour === h)
    return entry?.count || 0
  })
  const hourlyChartData = {
    labels: hourlyLabels,
    datasets: [
      {
        label: t("Acciones"),
        data: hourlyData,
        backgroundColor: "#14b8a688",
        borderColor: "#14b8a6",
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  }

  // Weekday bar chart
  const weekdayChartData = {
    labels: data.weekdayActivity.map(x => x.dayName.slice(0, 3)),
    datasets: [
      {
        label: t("Acciones"),
        data: data.weekdayActivity.map(x => x.count),
        backgroundColor: "#8b5cf688",
        borderColor: "#8b5cf6",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  }

  const chartOptionsWithLegend = {
    responsive: true,
    plugins: { legend: { display: true, position: "top" as const } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  }

  return (
    <div>
      {/* Header + period selector */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
        <h5 className="fw-bold mb-0">
          <i className="fas fa-chart-bar me-2 text-primary" />
          {t("Estadísticas de Uso")}
        </h5>
        <div className="btn-group mt-2 mt-sm-0">
          {[7, 14, 30, 60, 90].map(d => (
            <button
              key={d}
              className={`btn btn-sm ${
                days === d ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="row mb-4">
        <div className="col-sm-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center gap-3 p-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 48, height: 48, background: "#3b82f622" }}
              >
                <i className="fas fa-bolt fa-lg" style={{ color: "#3b82f6" }} />
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: "1.5rem" }}>
                  {data.totalActions.toLocaleString()}
                </div>
                <div className="text-muted small">{t("Total Acciones")}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center gap-3 p-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 48, height: 48, background: "#8b5cf622" }}
              >
                <i
                  className="fas fa-users fa-lg"
                  style={{ color: "#8b5cf6" }}
                />
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: "1.5rem" }}>
                  {data.totalUniqueUsers.toLocaleString()}
                </div>
                <div className="text-muted small">
                  {t("Usuarios Activos")}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center gap-3 p-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 48, height: 48, background: "#22c55e22" }}
              >
                <i
                  className="fas fa-building fa-lg"
                  style={{ color: "#22c55e" }}
                />
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: "1.5rem" }}>
                  {data.totalUniqueOrgs.toLocaleString()}
                </div>
                <div className="text-muted small">
                  {t("Organizaciones Activas")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily trend + Module ranking */}
      <div className="row mb-4">
        {/* Daily Trend */}
        <div className="col-lg-7 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="fas fa-chart-line me-2 text-info" />
                {t("Actividad Diaria")}
              </h6>
              {data.dailyTrend.length > 0 ? (
                <Line data={dailyChartData} options={chartOptionsWithLegend} />
              ) : (
                <p className="text-muted small">{t("Sin datos disponibles")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Module Ranking */}
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="fas fa-trophy me-2 text-warning" />
                {t("Ranking de Módulos")}
              </h6>
              <div className="d-flex flex-column gap-2">
                {data.moduleRanking.map((mod, i) => {
                  const pct = (mod.totalActions / maxModuleActions) * 100
                  const color = moduleColors[i % moduleColors.length]
                  const isExpanded = expandedModule === mod.module
                  const moduleActions = isExpanded
                    ? data.actionBreakdown.filter(a => a.module === mod.module)
                    : []

                  return (
                    <div key={mod.module}>
                      <div
                        className="d-flex align-items-center gap-2 p-1 rounded"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setExpandedModule(isExpanded ? null : mod.module)
                        }
                      >
                        <span
                          className="badge rounded-pill"
                          style={{
                            background: color + "22",
                            color: color,
                            minWidth: 28,
                          }}
                        >
                          #{i + 1}
                        </span>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="small fw-medium">
                              {mod.module}
                            </span>
                            <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                              {mod.totalActions.toLocaleString()} · {mod.uniqueUsers}u · {mod.uniqueOrgs}o
                            </span>
                          </div>
                          <div
                            className="progress"
                            style={{ height: 6 }}
                          >
                            <div
                              className="progress-bar"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: color,
                              }}
                            />
                          </div>
                        </div>
                        <i
                          className={`fas fa-chevron-${
                            isExpanded ? "up" : "down"
                          } text-muted`}
                          style={{ fontSize: "0.65rem" }}
                        />
                      </div>
                      {isExpanded && moduleActions.length > 0 && (
                        <div className="ms-5 mt-1 mb-1">
                          {moduleActions.map(a => (
                            <div
                              key={a.action}
                              className="d-flex justify-content-between text-muted"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <span>{a.action}</span>
                              <span className="fw-medium">
                                {a.count.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
                {data.moduleRanking.length === 0 && (
                  <p className="text-muted small">{t("Sin datos disponibles")}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hourly + Weekday charts */}
      <div className="row mb-4">
        <div className="col-lg-8 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="fas fa-clock me-2 text-success" />
                {t("Actividad por Hora")}
              </h6>
              <Bar data={hourlyChartData} options={chartOptions} />
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="fas fa-calendar-week me-2 text-secondary" />
                {t("Actividad por Día")}
              </h6>
              <Bar data={weekdayChartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Top Users + Top Organizations */}
      <div className="row mb-4">
        <div className="col-lg-6 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="fas fa-user me-2 text-primary" />
                {t("Top Usuarios")}
              </h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>{t("Usuario")}</th>
                      <th className="text-end">{t("Acciones")}</th>
                      <th className="text-end">{t("Módulos")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topUsers.map((u, i) => (
                      <tr
                        key={i}
                        style={{ cursor: "pointer" }}
                        onClick={() => setDetailView({ type: "user", userId: u.userId, userName: u.userName })}
                      >
                        <td className="text-muted">{i + 1}</td>
                        <td className="fw-medium text-primary">
                          {u.userName || u.userId.slice(0, 8)}
                        </td>
                        <td className="text-end">
                          {u.totalActions.toLocaleString()}
                        </td>
                        <td className="text-end">{u.modules}</td>
                      </tr>
                    ))}
                    {data.topUsers.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                          {t("Sin datos disponibles")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="fas fa-building me-2 text-success" />
                {t("Top Organizaciones")}
              </h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>{t("Organización")}</th>
                      <th className="text-end">{t("Acciones")}</th>
                      <th className="text-end">{t("Usuarios")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topOrganizations.map((o, i) => (
                      <tr
                        key={i}
                        style={{ cursor: "pointer" }}
                        onClick={() => setDetailView({ type: "org", organizationId: o.organizationId, organizationName: o.organizationName })}
                      >
                        <td className="text-muted">{i + 1}</td>
                        <td className="fw-medium text-primary">{o.organizationName}</td>
                        <td className="text-end">
                          {o.totalActions.toLocaleString()}
                        </td>
                        <td className="text-end">{o.uniqueUsers}</td>
                      </tr>
                    ))}
                    {data.topOrganizations.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                          {t("Sin datos disponibles")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminUsageStatisticsComponent
